import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, LinearProgress, Typography } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { DropzoneArea } from 'material-ui-dropzone';
import { read, utils, writeFileXLSX } from 'xlsx';
import './Import.css';
import IssuesTab from './IssuesTab';
import FileSummary from './FileSummary';
import { importEmployeeBankingByExcel } from 'src/services/employeeService';

const ImportModalForm = ({ toggle, getData }) => {
  const [fileName, setFileName] = useState('');
  const [errors, setErrors] = useState('');
  const [fileTemplateStatus, setFileTemplateStatus] = useState(false);
  const [tableValues, setTableValues] = useState<any>([]);
  const [headers] = useState([
    'EmployeeID',
    'NIC',
    'Bank Name',
    'Branch Name',
    'Branch Code',
    'Account Name',
    'Account Number',
    'Salary password'
  ]);

  const [requiredFields] = useState([
    'EmployeeID',
    'NIC',
    'Bank Name',
    'Branch Name',
    'Branch Code',
    'Account Name',
    'Account Number'
  ]);

  const [attachment, setAttachment] = useState<any>({});

  const [timerStarted, setTimerStarted] = useState(false);
  const [progress, setProgress] = useState(0);

  const [successRecords, setSuccessRecords] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [invalidValues, setInvalidValues] = useState([]);
  const [invalidRows, setInvalidRows] = useState(0);

  const [openIssues, setOpenIssues] = useState(false);

  const onImport = () => {
    if (attachment) {
      importEmployeeBankingByExcel(attachment.file).then((res) => {
        const data = res.data;
        setSuccessRecords(data?.successRecords);
        setTotalRecords(data?.totalRecords);
        if (data?.errors?.length > 0) {
          const entries = Object.entries(data.errors);
          const allErrors = entries.map((entry) => {
            return {
              error: entry[1],
              row: Number(entry[0])
            };
          });
          setInvalidValues([...allErrors]);
        }
        getData();
      });
    }
  };

  const handleFileChange = (files) => {
    setTableValues([]);
    setFileTemplateStatus(false);
    setProgress(0);
    if (files[0]) {
      setAttachment({ file: files[0] });
      setFileName(files[0].name);
      const reader = new FileReader();
      if (reader.readAsBinaryString) {
        reader.onload = (e) => {
          const workbook = read(reader.result, { type: 'binary' });
          const headerSheets = workbook.Sheets[workbook.SheetNames[0]];
          const excelHeaders: any = utils.sheet_to_json(headerSheets, {
            header: 1
          });

          const firstSheet = workbook.SheetNames[0];
          const excelRows = utils.sheet_to_json(workbook.Sheets[firstSheet]);

          if (
            !excelHeaders[0]?.every((value, index) => value === headers[index])
          ) {
            setFileTemplateStatus(false);
            setErrors('File template is invalid');
          } else {
            setTableValues(excelRows);
            setFileTemplateStatus(true);
          }
        };
        setProgress(20);
        reader.readAsBinaryString(files[0]);
      }
    }
  };

  useEffect(() => {
    if (fileTemplateStatus) {
      setErrors('');
    }
  }, [fileTemplateStatus]);

  useEffect(() => {
    setProgress(0);
  }, [fileName]);

  const toggleIssues = () => {
    setOpenIssues(!openIssues);
  };

  useEffect(() => {
    setSuccessRecords(tableValues.length - invalidRows);
    setTotalRecords(tableValues.length);
  }, [tableValues, invalidRows]);

  useEffect(() => {
    if (tableValues.length > 0 && fileTemplateStatus) {
      let emptyRowArr = [];
      let invalidEmails = [];
      let invalidRow = 0;
      tableValues.forEach((tab) => {
        const rowNum = tab.__rowNum__;
        setProgress(40);
        const entries = Object.entries(tab);
        const emptyFields = requiredFields.filter((head) => {
          if (!entries.some((val) => val[0] === head)) return head;
        });

        const emptyFieldsRows = emptyFields.map((field) => {
          return {
            error: `${field} is Required`,
            row: rowNum
          };
        });

        if (emptyFieldsRows.length > 0) {
          emptyRowArr = [...emptyRowArr, ...emptyFieldsRows];
        }

        setTimeout(() => {
          setProgress(70);
        }, 500);

        if (emptyFieldsRows.length > 0 || invalidEmails.length > 0) {
          invalidRow = invalidRow + 1;
        }
      });

      setTimeout(() => {
        setProgress(100);
      }, 1000);

      setInvalidValues([...emptyRowArr, ...invalidEmails]);
      setInvalidRows(invalidRow);
    }
  }, [tableValues, requiredFields, fileTemplateStatus]);

  return (
    <>
      <Grid sx={{ px: 2, mt: '0.5rem' }} container spacing={2}>
        <Grid item xs={12}>
          <Typography
            style={{
              color: '#223354',
              fontWeight: '700',
              fontSize: '15px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
            }}
            sx={{ pb: 1 }}
            variant="h4"
          >
            Import Banking Information
          </Typography>
        </Grid>
        {!fileTemplateStatus && (
          <Grid display="flex" alignItems="center" item xs={12}>
            <Grid item xs={9}>
              <Typography sx={{ color: 'red' }} variant="body2">
                Please make sure to use the given template
              </Typography>
            </Grid>
            <Grid display="flex" justifyContent="end" item xs={3}>
              <Button
                id="btn-bankInfoDownload"
                sx={{
                  mt: { xs: 2, md: 0 },
                  fontSize: '11px',
                  fontWeight: 'normal'
                }}
                variant="outlined"
                startIcon={<GetAppIcon fontSize="small" />}
                href={
                  process.env.PUBLIC_URL +
                  '/templates/Bank Information Template.xlsx'
                }
                download="Bank Information Template"
              >
                Download Template
              </Button>
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <DropzoneArea
            acceptedFiles={['.xlsx', '.xls']}
            onChange={(e) => handleFileChange(e)}
            showPreviews={false}
            showPreviewsInDropzone={false}
            filesLimit={1}
            showAlerts={false}
          />
          {errors && (
            <Typography sx={{ color: 'red' }} variant="body2">
              {errors}
            </Typography>
          )}
        </Grid>
        {fileTemplateStatus && (
          <Grid item xs={12}>
            <Box
              className="loader-container"
              display="flex"
              alignItems="center"
              sx={{ width: '100%' }}
            >
              <img
                src={process.env.PUBLIC_URL + '/img/excelsheet.jpg'}
                alt=""
                style={{
                  width: '48px',
                  height: '48px'
                }}
              />
              <Box sx={{ width: '100%' }}>
                <Grid item display="flex" justifyContent="between">
                  <p style={{ marginBottom: 0 }}>{fileName}</p>
                  <p style={{ marginBottom: 0, marginLeft: 'auto' }}>
                    {progress}
                  </p>
                </Grid>

                <LinearProgress variant="determinate" value={progress} />
              </Box>
            </Box>
          </Grid>
        )}
        {tableValues?.length > 0 && (
          <FileSummary
            tableValues={tableValues}
            successRecords={successRecords}
            totalRecords={totalRecords}
            toggleIssues={toggleIssues}
            invalidRows={invalidRows}
            errorsCount={totalRecords - successRecords || 0}
          />
        )}
        <Grid container spacing={2}>
          <Grid item xs={6}></Grid>
          <Grid
            style={{
              marginTop: '3rem',
              marginBottom: '1rem'
            }}
            item
            sx={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}
            xs={6}
          >
            <Button
              id="btn-bankInfo-toggle"
              style={{
                minWidth: '64px',
                padding: '6px 16px',
                backgroundColor: '#5569ff',
                paddingLeft: '16px',
                paddingRight: '16px',
                color: '#FFFFFF',
                borderRadius: '6px',
                width: '30%'
              }}
              variant="outlined"
              onClick={() => toggle()}
            >
              Close
            </Button>
            <Button
              id="btn-bankInfo-onImport"
              style={{
                minWidth: '64px',
                padding: '6px 16px',
                backgroundColor: '#5569ff',
                paddingLeft: '16px',
                paddingRight: '16px',
                color: '#FFFFFF',
                borderRadius: '6px',
                marginLeft: '1rem',
                width: '30%'
              }}
              variant="contained"
              onClick={() => onImport()}
            >
              Import
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <IssuesTab
        toggle={toggleIssues}
        open={openIssues}
        invalidValues={invalidValues}
      />
    </>
  );
};

export default ImportModalForm;
